import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { fetchImageAsync, fetchMCAnswersAsync } from '../../admin/questions/QuestionsView/questionSlice';
import { Box, Button, Checkbox, Grid, Paper, TextField, Typography } from '@mui/material';
import { AnswerCreatePayloadRequest, AnswerUpdatePayloadRequest, MultipleChoiceAnswer } from '../../../generated/api';
import { QuestionCardProps } from './types';
import { selectAttempts } from './attemptSlice';
import { createAnswerAsync, destroyAnswerAsync, selectAnswers, updateAnswerAsync } from './answerSlice';

const QuestionCard = ({ question, setUnsavedChanges }: QuestionCardProps) => {
  const dispatch = useAppDispatch();
  const { questionIndex } = useParams();
  const attemptState = useAppSelector(selectAttempts);
  const answerState = useAppSelector(selectAnswers);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [mcOptions, setMcOptions] = useState<MultipleChoiceAnswer[]>([]);
  const [answer, setAnswer] = useState<string | null>(null);
  const [mcAnswer, setMcAnswer] = useState<number | null>(null);

  const disabled = attemptState.loading || attemptState.finished;

  useEffect(() => {
    setImageUrl(null);
    setMcOptions([]);

    if (question.image) {
      dispatch(fetchImageAsync(question.id))
        .unwrap()
        .then((value) => setImageUrl(URL.createObjectURL(value)));
    }
    if (question.type === 'MC') {
      dispatch(fetchMCAnswersAsync({ questionId: question.id.toString() }))
        .unwrap()
        .then((value) => setMcOptions(value));
    }
  }, [question]);

  useEffect(() => {
    setAnswer('');
    setMcAnswer(null);
    const answer = answerState.answers.find((a) => a.question === question.id);
    setAnswer(answer?.text ?? '');
    setMcAnswer(answer?.mcAnswer ?? null);
  }, [answerState.answers, question]);

  useEffect(() => {
    const prevAnswer = answerState.answers.find((a) => a.question === question.id);
    setUnsavedChanges(false);
    if (prevAnswer && answer === prevAnswer.text) return;
    if (!prevAnswer && !answer) return;
    setUnsavedChanges(true);
  }, [answer]);

  const handleSubmit = () => {
    if (!attemptState.attempt) return;
    setUnsavedChanges(false);
    const prevAnswer = answerState.answers.find((a) => a.question === question.id);
    if (prevAnswer && !answer) {
      dispatch(destroyAnswerAsync(prevAnswer.id));
      return;
    }
    if (!answer) return;
    const payload: AnswerCreatePayloadRequest | AnswerUpdatePayloadRequest = {
      attempt: attemptState.attempt.id,
      question: question.id,
      text: answer ?? '',
      mcAnswer: mcAnswer ?? undefined
    };
    if (prevAnswer) {
      dispatch(updateAnswerAsync({ id: prevAnswer.id, data: payload }));
    } else {
      dispatch(createAnswerAsync(payload));
    }
  };

  if (!question || !questionIndex) return null;
  return (
    <Box>
      <Paper sx={{ paddingX: 1 }}>
        <Typography variant="h5" align="center">
          {question.name}
        </Typography>
        {imageUrl && (
          <Box display="flex" justifyContent="center">
            <img
              src={imageUrl}
              alt={'question_image'}
              style={{ width: 'auto', height: '40vh', maxWidth: '100%', objectFit: 'contain' }}
            />
          </Box>
        )}
        <Typography align="center" sx={{ whiteSpace: 'pre-line' }}>
          {question.description}
        </Typography>
        {question.type === 'MC' ? (
          <Grid
            container
            justifyContent="center"
            borderTop={1}
            borderColor="primary.main"
            sx={{ '> .MuiGrid-item:nth-of-type(even)': { borderLeft: 1, borderColor: 'primary.main' } }}
          >
            {mcOptions.map((option) => (
              <Grid item key={option.id} xs={6} borderBottom={1} borderColor="primary.main">
                <Box display="flex" flexDirection="row" alignItems="center">
                  <Checkbox
                    checked={mcAnswer === option.id}
                    onChange={({ target }) => {
                      setAnswer(target.checked ? option.text : null);
                      setMcAnswer(target.checked ? option.id : null);
                      setUnsavedChanges(true);
                    }}
                    disabled={disabled}
                  />
                  <Typography width="100%" padding="none">
                    {option.text}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box padding={1}>
            <TextField
              label="answer"
              fullWidth
              value={answer ?? ''}
              onChange={({ target }) => {
                setAnswer(target.value);
                setUnsavedChanges(true);
              }}
              disabled={disabled}
            />
          </Box>
        )}
        <Box display="flex" justifyContent="center" paddingY={1}>
          <Button color="success" variant="contained" onClick={handleSubmit} disabled={disabled}>
            Submit
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default QuestionCard;
